import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

// ===================================================================================
// Modernized Login Page Component
// ===================================================================================
const LoginPage = ({ role }) => {
    const navigate = useNavigate();

    const [email, setEmail] = useState(
        role === 'admin' ? process.env.REACT_APP_ADMIN_EMAIL || '' : ''
    );
    const [password, setPassword] = useState(
        role === 'admin' ? process.env.REACT_APP_ADMIN_PASSWORD || '' : ''
    );
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isPasswordVisible, setIsPasswordVisible] = useState(false);

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:8080";

    const pageTitle = role === 'admin' ? 'Admin Portal' : 'User Login';
    const pageSubtitle = role === 'admin' ? 'Sign in to manage your system' : 'Welcome back! Please sign in.';

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email || !password) {
            setError('Email and password are required.');
            return;
        }
        setIsLoading(true);
        setError('');

        try {
            const response = await axios.post(`${API_BASE_URL}/api/auth/login`, {
                email,
                password,
            });

            const { token, role: userRole, userId } = response.data;

            localStorage.setItem('authToken', token);
            localStorage.setItem('userRole', userRole);
            localStorage.setItem('userId', userId);

            if (userRole && userRole.toUpperCase() === 'ADMIN') {
                navigate('/admin/dashboard');
            } else {
                navigate('/dashboard');
            }

        } catch (err) {
            console.error("Login API Error:", err);
            if (err.response && err.response.data && err.response.data.message) {
                setError(err.response.data.message);
            } else {
                setError('Login failed. Please check credentials and try again.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="login-container">
            <div className="login-backdrop"></div>
            <div className="login-card">
                <div className="login-header">
                    <div className="login-logo">
                        <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect width="48" height="48" rx="12" fill="url(#gradient)"/>
                            <path d="M14 24C14 18.477 18.477 14 24 14C29.523 14 34 18.477 34 24" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
                            <path d="M20 28C20 24.686 22.686 22 26 22C29.314 22 32 24.686 32 28" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
                            <circle cx="24" cy="24" r="3" fill="white"/>
                            <circle cx="26" cy="28" r="2" fill="white"/>
                            <defs>
                                <linearGradient id="gradient" x1="0" y1="0" x2="48" y2="48" gradientUnits="userSpaceOnUse">
                                    <stop stopColor="#6366F1"/>
                                    <stop offset="1" stopColor="#8B5CF6"/>
                                </linearGradient>
                            </defs>
                        </svg>
                    </div>
                    <h1 className="login-title">{pageTitle}</h1>
                    <p className="login-subtitle">{pageSubtitle}</p>
                </div>

                <form onSubmit={handleSubmit} className="login-form">
                    {error && (
                        <div className="login-error">
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/>
                            </svg>
                            {error}
                        </div>
                    )}
                    
                    <div className="form-group">
                        <label htmlFor="email">Email Address</label>
                        <div className="input-wrapper">
                            <span className="input-icon">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                                    <polyline points="22,6 12,13 2,6"></polyline>
                                </svg>
                            </span>
                            <input
                                type="email"
                                id="email"
                                placeholder="Enter your email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="login-input"
                                required
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="password">Password</label>
                        <div className="input-wrapper">
                            <span className="input-icon">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                                    <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                                </svg>
                            </span>
                            <input
                                type={isPasswordVisible ? "text" : "password"}
                                id="password"
                                placeholder="Enter your password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="login-input"
                                required
                            />
                            <button
                                type="button"
                                className="password-toggle"
                                onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                                tabIndex={-1}
                            >
                                {isPasswordVisible ? (
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                                        <line x1="1" y1="1" x2="23" y2="23"></line>
                                    </svg>
                                ) : (
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                        <circle cx="12" cy="12" r="3"></circle>
                                    </svg>
                                )}
                            </button>
                        </div>
                    </div>

                    <button
                        type="submit"
                        className="login-button"
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <span className="login-spinner"></span>
                        ) : (
                            <>
                                Sign In
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="5" y1="12" x2="19" y2="12"></line>
                                    <polyline points="12 5 19 12 12 19"></polyline>
                                </svg>
                            </>
                        )}
                    </button>
                </form>

                {role === 'user' && (
                    <div className="login-footer">
                        <span>Don't have an account?</span>
                        <a href="/register" className="login-link">Sign Up</a>
                    </div>
                )}
            </div>

            <style>{`
                .login-container {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    min-height: 100vh;
                    background: #0F1419;
                    position: relative;
                    overflow: hidden;
                }
                
                .login-backdrop {
                    position: absolute;
                    inset: 0;
                    background: 
                        radial-gradient(ellipse at 20% 20%, rgba(99, 102, 241, 0.15) 0%, transparent 50%),
                        radial-gradient(ellipse at 80% 80%, rgba(139, 92, 246, 0.1) 0%, transparent 50%),
                        radial-gradient(ellipse at 50% 50%, rgba(99, 102, 241, 0.05) 0%, transparent 70%);
                }
                
                .login-card {
                    position: relative;
                    width: 100%;
                    max-width: 420px;
                    padding: 40px;
                    background: rgba(30, 37, 54, 0.8);
                    backdrop-filter: blur(20px);
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    border-radius: 24px;
                    box-shadow: 
                        0 25px 50px -12px rgba(0, 0, 0, 0.5),
                        0 0 0 1px rgba(255, 255, 255, 0.05) inset;
                    animation: slideUp 0.5s ease-out;
                }
                
                .login-header {
                    text-align: center;
                    margin-bottom: 32px;
                }
                
                .login-logo {
                    margin-bottom: 20px;
                    display: inline-block;
                    animation: scaleIn 0.5s ease-out 0.2s both;
                }
                
                .login-title {
                    font-size: 28px;
                    font-weight: 700;
                    color: #F7FAFC;
                    margin: 0 0 8px 0;
                    animation: fadeIn 0.5s ease-out 0.3s both;
                }
                
                .login-subtitle {
                    font-size: 15px;
                    color: #A0AEC0;
                    margin: 0;
                    animation: fadeIn 0.5s ease-out 0.4s both;
                }
                
                .login-form {
                    display: flex;
                    flex-direction: column;
                    gap: 20px;
                    animation: fadeIn 0.5s ease-out 0.5s both;
                }
                
                .login-error {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    padding: 14px 16px;
                    background: rgba(239, 68, 68, 0.15);
                    border: 1px solid rgba(239, 68, 68, 0.3);
                    border-radius: 12px;
                    color: #F87171;
                    font-size: 14px;
                    animation: shake 0.5s ease-out;
                }
                
                @keyframes shake {
                    0%, 100% { transform: translateX(0); }
                    20%, 60% { transform: translateX(-5px); }
                    40%, 80% { transform: translateX(5px); }
                }
                
                .form-group {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }
                
                .form-group label {
                    font-size: 14px;
                    font-weight: 500;
                    color: #A0AEC0;
                    margin: 0;
                }
                
                .input-wrapper {
                    position: relative;
                    display: flex;
                    align-items: center;
                }
                
                .input-icon {
                    position: absolute;
                    left: 14px;
                    color: #64748B;
                    display: flex;
                    align-items: center;
                    pointer-events: none;
                }
                
                .login-input {
                    width: 100%;
                    padding: 14px 14px 14px 46px;
                    background: rgba(36, 43, 61, 0.8);
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    border-radius: 12px;
                    color: #F7FAFC;
                    font-size: 15px;
                    transition: all 0.2s ease;
                }
                
                .login-input::placeholder {
                    color: #64748B;
                }
                
                .login-input:focus {
                    border-color: #6366F1;
                    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.2);
                    outline: none;
                }
                
                .password-toggle {
                    position: absolute;
                    right: 14px;
                    background: none;
                    border: none;
                    color: #64748B;
                    cursor: pointer;
                    padding: 4px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: color 0.2s ease;
                }
                
                .password-toggle:hover {
                    color: #A0AEC0;
                }
                
                .login-button {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 10px;
                    width: 100%;
                    padding: 16px;
                    background: linear-gradient(135deg, #6366F1 0%, #4F46E5 100%);
                    border: none;
                    border-radius: 12px;
                    color: #FFFFFF;
                    font-size: 16px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    margin-top: 8px;
                    box-shadow: 0 4px 15px rgba(99, 102, 241, 0.4);
                }
                
                .login-button:hover:not(:disabled) {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 25px rgba(99, 102, 241, 0.5);
                }
                
                .login-button:active:not(:disabled) {
                    transform: translateY(0);
                }
                
                .login-button:disabled {
                    opacity: 0.7;
                    cursor: not-allowed;
                }
                
                .login-spinner {
                    width: 22px;
                    height: 22px;
                    border: 3px solid rgba(255, 255, 255, 0.3);
                    border-top-color: #FFFFFF;
                    border-radius: 50%;
                    animation: spin 0.8s linear infinite;
                }
                
                .login-footer {
                    text-align: center;
                    margin-top: 28px;
                    font-size: 14px;
                    color: #64748B;
                    animation: fadeIn 0.5s ease-out 0.6s both;
                }
                
                .login-link {
                    color: #6366F1;
                    font-weight: 600;
                    text-decoration: none;
                    margin-left: 6px;
                    transition: color 0.2s ease;
                }
                
                .login-link:hover {
                    color: #818CF8;
                }
                
                @keyframes slideUp {
                    from {
                        opacity: 0;
                        transform: translateY(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                
                @keyframes scaleIn {
                    from {
                        opacity: 0;
                        transform: scale(0.8);
                    }
                    to {
                        opacity: 1;
                        transform: scale(1);
                    }
                }
                
                @keyframes spin {
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};

export default LoginPage;
